<template>
    <div v-if="!hideProduct"
         :class="[
             'relative flex flex-col md:flex-row border rounded-lg overflow-hidden shadow-md bg-gray-50',
             isSoldOut ? 'opacity-70 scale-95 transform' : ''
         ]">
        <div v-if="showPopularRibbon || showLimitedSpacesRibbon || showElectricRibbon || showMostFlexibleRibbon" class="absolute right-[-40px] top-[25px] w-[200px] z-10 ml-5">
            <div :class="[ribbonBgColorClass]"
                 class="text-center font-semibold text-xs py-1 px-4 text-white uppercase shadow-md transform rotate-45 whitespace-normal leading-tight">
               <span v-if="showLimitedSpacesRibbon && this.max_per_day > 0" class="uppercase block">
               <template v-if="this.current_per_day < this.max_per_day">
                    {{ this.max_per_day - this.current_per_day }} {{ this.max_per_day - this.current_per_day === 1 ? 'SPACE' : 'SPACES' }}<br>REMAINING
                </template>
                <template v-else>
                    SOLD<br>OUT
                </template>
             </span>
                <span v-else-if="showPopularRibbon" class="uppercase block">MOST<br>POPULAR</span>
                <span v-else-if="showElectricRibbon" class="uppercase block">{{ electricAvailable }} {{ electricAvailable === 1 ? 'SPACE' : 'SPACES' }}<br>REMAINING</span>
                <span v-else-if="showMostFlexibleRibbon" class="uppercase block">MOST<br>FLEXIBLE</span>
            </div>
        </div>

        <div class="md:flex-shrink-0 w-full md:w-64 relative">
            <div class="pb-[75%] md:pb-0 md:h-full">
                <img :src="image" alt="Product Image" class="absolute inset-0 w-full h-full object-cover">
            </div>
        </div>

        <div class="flex-grow p-6 flex flex-col justify-center b-white">
            <h2 :class="[productTextColorClass]" class="text-2xl font-extrabold mb-2 uppercase">{{ name }}</h2>
            <p v-html="description" class="text-gray-700 text-sm mb-4 leading-relaxed"></p>

            <div class="text-gray-600 text-xs mb-4">
                Open 365 days a year, 7 days a week, 24 hours a day.<br>
                Secured with CCTV cameras, full fencing and automatic floodlights.
            </div>

            <hr class="border-t border-gray-200 my-4">

            <p v-if="daysLeft >= 0" class="text-red-500 font-bold text-sm">Limited time offer: {{ daysLeft }} days left</p>
        </div>

        <div class="md:flex-shrink-0 w-full md:w-64 bg-gray-50 p-6 flex flex-col items-center justify-between text-center md:ml-4 md:mr-[60px] md:mt-[10px]">
            <div class="w-full">
                <span class="text-4xl font-extrabold text-gray-800">£{{ mainPrice }}</span>

                <div class="text-gray-600 text-sm mt-1">
                   <span v-for="(item, key, index) in filteredOtherPrices" :key="key" class="mr-2 capitalize">
                    {{ key }} <span class="font-bold">£{{ item.adjusted }}</span><template v-if="index !== Object.keys(filteredOtherPrices).length - 1"> |</template>
                </span>
                </div>
            </div>

            <a v-if="this.max_per_day === null || this.current_per_day === null || this.current_per_day < this.max_per_day"
               :href="`/quick-booking/${id}`"
               :class="[productBgColorClass]"
               class="mt-6 text-white font-bold py-3 px-8 rounded-md shadow-lg text-lg uppercase transition-colors duration-200 ease-in-out hover:opacity-90 w-full md:w-auto">
                Select & Pay
            </a>
            <span v-else
                  class="mt-6 bg-gray-400 text-white font-bold py-3 px-8 rounded-md shadow-lg text-lg uppercase cursor-not-allowed w-full md:w-auto">
                SOLD OUT
            </span>

            <p class="text-gray-500 text-xs mt-4 font-bold">Transfer time of 5 mins by free shuttle bus to airport terminal.</p>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        id: {
            type: Number,
            required: true,
        },
        name: {
            type: String,
            required: true,
        },
        description: {
            type: String,
            required: true,
        },
        image: {
            type: String,
            required: true,
        },
        price: {
            type: Number,
            required: true,
        },
        start_ts: {
            type: String,
            required: false,
        },
        end_ts: {
            type: String,
            required: false,
        },
        prices: {
            type: Object,
            required: false,
        },
        operation: {
            type: String,
            required: false,
        },
        active: {
            type: Boolean,
            required: false,
        },
        current_per_day: {
            type: Number,
            required: false,
        },
        max_per_day: {
            type: Number,
            required: false,
        },
        electricAvailable: {
            type: Number,
            required: false,
            default: 6,
        },
    },
    computed: {
        ribbonBgColorClass() {
            const productName = this.name.toLowerCase();
            if (productName.includes('super saver')) {
                return 'bg-red-600';
            } if (productName.includes('lowcost saver')) {
                return 'bg-orange-600';
            } if (productName.includes('flying scot')) {
                return 'bg-blue-600';
            } if (productName.includes('electric')) {
                return 'bg-green-600';
            }
            return 'bg-orange-600';
        },

        showLimitedSpacesRibbon() {
            return this.max_per_day > 0;
        },
        daysLeft() {
            if (this.start_ts && this.end_ts) {
                const endDate = new Date(this.end_ts);
                const currentDate = new Date();
                const timeDiff = endDate - currentDate;
                const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
                return daysLeft >= 0 ? daysLeft : -1;
            }
            return -1;
        },
        showPopularRibbon() {
            return this.name.toLowerCase().includes('lowcost saver');
        },
        filteredOtherPrices() {
            const filteredPrices = {};
            for (const key in this.otherPrices) {
                if (key !== '4x4' && this.otherPrices.hasOwnProperty(key)) {
                    filteredPrices[key] = this.otherPrices[key];
                }
            }
            return filteredPrices;
        },
        showElectricRibbon() {
            return this.name.toLowerCase().includes('electric');
        },
        showMostFlexibleRibbon() {
            return this.name.toLowerCase().includes('flying scot');
        },
        mainPrice() {
            return this.prices && this.prices.car && typeof this.prices.car.adjusted === 'number'
                ? this.prices.car.adjusted
                : this.price;
        },
        otherPrices() {
            const filteredPrices = {};
            for (const key in this.prices) {
                if (key !== 'car' && this.prices.hasOwnProperty(key)) {
                    filteredPrices[key] = this.prices[key];
                }
            }
            return filteredPrices;
        },
        hideProduct() {
            return this.active === false || (this.max_per_day !== null && this.current_per_day >= this.max_per_day) || (this.start_ts && this.end_ts && this.daysLeft < 0);
        },
        showBanner() {
            return this.max_per_day > 0;
        },

        remainingSpaces() {
            return this.max_per_day - this.current_per_day;
        },
        isSoldOut() {
            return this.max_per_day !== null && this.current_per_day !== null && this.current_per_day >= this.max_per_day;
        },
        productTextColorClass() {
            const productName = this.name.toLowerCase();
            if (productName.includes('super saver')) {
                return 'text-red-600'; // color for Super Saver
            } if (productName.includes('low cost')) {
                return 'text-orange-600'; // color for Low Cost Saver
            } if (productName.includes('flying scot')) {
                return 'text-blue-600'; // for Flying Scot Express
            } if (productName.includes('electric')) {
                return 'text-green-600'; // color for Electric Bay
            }
            return 'text-orange-600'; // Default color if no match
        },
        productBgColorClass() {
            const productName = this.name.toLowerCase();
            if (productName.includes('super saver')) {
                return 'bg-red-600'; // Super Saver is now red
            } if (productName.includes('low cost')) {
                return 'bg-orange-600'; // Low Cost Saver remains orange
            } if (productName.includes('flying scot')) {
                return 'bg-blue-600'; // Flying Scot Express is now blue
            } if (productName.includes('electric')) {
                return 'bg-green-600'; // Electric Bay remains green
            }
            return 'bg-orange-600'; // Default color if no match
        },
    },
};
</script>
